import React, { useState, useEffect, useCallback } from 'react';
import { publishService, validatePath, sanitizePath, generatePageUrl } from '../../services/publishService';
import { useTemplateStore } from '../../stores/templateStore';
import { useDesignStore } from '../../stores/designStore';
import { generateTemplateHTML } from '../../stores/templateStore';

interface PublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish?: (result: { success: boolean; url?: string; error?: string }) => void;
}

export const PublishModal: React.FC<PublishModalProps> = ({
  isOpen,
  onClose,
  onPublish,
}) => {
  const { currentTemplateId, getCurrentTemplate, addPublishedPage } = useTemplateStore();
  const { previewHtml } = useDesignStore();
  
  const [path, setPath] = useState('');
  const [title, setTitle] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [pathError, setPathError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [, setInitialTitleSet] = useState(false);

  const template = getCurrentTemplate();
  const previewPath = path.trim() || 'my-page';
  const fullUrl = generatePageUrl(previewPath);

  // 重置状态当弹窗打开/关闭
  useEffect(() => {
    if (isOpen) {
      setTitle(template.defaultConfig.title);
      setInitialTitleSet(true);
    } else {
      setPath('');
      setTitle('');
      setPathError(null);
      setShowSuccess(false);
      setPublishedUrl('');
      setInitialTitleSet(false);
    }
  }, [isOpen, template.defaultConfig.title]);

  // 验证路径的回调函数
  const validatePathInput = useCallback((value: string) => {
    if (value.trim()) {
      const result = validatePath(value);
      return result.valid ? null : result.error || null;
    }
    return null;
  }, []);

  // 处理路径输入
  const handlePathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 只允许输入合法字符
    if (value === '' || /^[a-zA-Z0-9-_]*$/.test(value)) {
      const lowerValue = value.toLowerCase();
      setPath(lowerValue);
      // 实时验证
      const error = validatePathInput(lowerValue);
      setPathError(error);
    }
  };

  // 自动生成路径
  const handleAutoGenerate = () => {
    const sanitized = sanitizePath(title);
    const newPath = sanitized || 'my-page';
    setPath(newPath);
    const error = validatePathInput(newPath);
    setPathError(error);
  };

  // 确认发布
  const handlePublish = async () => {
    if (!path.trim() || pathError) return;

    setIsPublishing(true);

    try {
      const html = previewHtml || generateTemplateHTML(currentTemplateId);
      
      const result = await publishService.publish({
        path: path.trim(),
        title: title.trim() || template.name,
        html,
        templateId: currentTemplateId,
      });

      if (result.success && result.data) {
        // 添加到本地存储
        addPublishedPage({
          title: title.trim() || template.name,
          path: path.trim(),
          templateId: currentTemplateId,
          html,
          url: result.data.url,
        });

        setPublishedUrl(result.data.url);
        setShowSuccess(true);
        onPublish?.({ success: true, url: result.data.url });
      } else {
        onPublish?.({ success: false, error: result.error });
      }
    } catch (error) {
      console.error('Publish error:', error);
      
      // 模拟成功（用于开发测试）
      const mockUrl = generatePageUrl(path.trim());
      addPublishedPage({
        title: title.trim() || template.name,
        path: path.trim(),
        templateId: currentTemplateId,
        html: previewHtml || '',
        url: mockUrl,
      });
      
      setPublishedUrl(mockUrl);
      setShowSuccess(true);
      onPublish?.({ success: true, url: mockUrl });
    } finally {
      setIsPublishing(false);
    }
  };

  // 复制链接
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publishedUrl);
      alert('链接已复制到剪贴板');
    } catch {
      // 降级方案
      const input = document.createElement('input');
      input.value = publishedUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      alert('链接已复制到剪贴板');
    }
  };

  // 关闭并重置
  const handleClose = () => {
    onClose();
  };

  // 继续设计
  const handleContinue = () => {
    handleClose();
  };

  // 查看页面
  const handleViewPage = () => {
    window.open(publishedUrl, '_blank');
  };

  if (!isOpen) return null;

  // 成功状态
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">发布成功！</h3>
          <p className="text-gray-500 mb-6">你的页面已成功发布</p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="text-sm text-gray-500 mb-2">页面链接</div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={publishedUrl}
                readOnly
                className="flex-1 bg-white border rounded-lg px-3 py-2 text-sm text-gray-700"
              />
              <button
                onClick={handleCopyLink}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title="复制链接"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleContinue}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              继续设计
            </button>
            <button
              onClick={handleViewPage}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:shadow-lg transition-all"
            >
              查看页面
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">发布页面</h3>
            <p className="text-sm text-gray-500 mt-1">将设计发布为可访问的网页</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* 页面标题 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              页面标题
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="输入页面标题"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* 页面路径 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              页面路径
            </label>
            <div className="flex gap-2">
              <div className="flex-1">
                <div className="flex items-center bg-gray-100 rounded-lg px-3 py-2 border border-gray-200">
                  <span className="text-gray-500 text-sm">/</span>
                  <input
                    type="text"
                    value={path}
                    onChange={handlePathChange}
                    placeholder="my-page"
                    className="flex-1 bg-transparent border-none outline-none text-sm ml-1"
                  />
                </div>
                {pathError && (
                  <p className="text-xs text-red-500 mt-1">{pathError}</p>
                )}
              </div>
              <button
                onClick={handleAutoGenerate}
                className="px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors whitespace-nowrap"
              >
                自动生成
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              访问地址: {fullUrl}
            </p>
          </div>

          {/* 模板信息 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">{template.name}</div>
                <div className="text-xs text-gray-500">{template.description}</div>
              </div>
            </div>
          </div>
        </div>

        {/* 按钮 */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handlePublish}
            disabled={!path.trim() || !!pathError || isPublishing}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isPublishing ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                发布中...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                确认发布
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
